<script setup lang="ts">
import { computed, ref } from "vue";
import Icon from "./Icon.vue";
import { TAG_COLORS, inkOn, sameColor } from "../composables/tagColors";
import { t, type MessageKey } from "../i18n";
import { vTooltip } from "../directives/tooltip";

/**
 * A tag's colour: the ten ready ones as round swatches and a rainbow one that opens the browser's
 * full colour picker for any other.
 */
const model = defineModel<string>({ required: true });
const picker = ref<HTMLInputElement>();
const custom = computed(
  () => !TAG_COLORS.some((c) => sameColor(c.hex, model.value)),
);

function openPicker() {
  const input = picker.value;
  if (!input) return;
  try {
    input.showPicker();
  } catch {
    input.click();
  }
}
</script>
<template>
  <div class="color-swatches" role="radiogroup" :aria-label="t('tags.color')">
    <button
      v-for="c in TAG_COLORS"
      :key="c.key"
      type="button"
      role="radio"
      class="color-swatch"
      :style="{ '--swatch': c.hex, '--swatch-ink': inkOn(c.hex) }"
      :aria-checked="sameColor(c.hex, model)"
      :aria-label="t(`tags.color.${c.key}` as MessageKey)"
      v-tooltip="t(`tags.color.${c.key}` as MessageKey)"
      @click="model = c.hex"
    >
      <Icon v-if="sameColor(c.hex, model)" name="check" :size="16" />
    </button>
    <button
      type="button"
      role="radio"
      class="color-swatch custom"
      :class="{ picked: custom }"
      :style="
        custom ? { '--swatch': model, '--swatch-ink': inkOn(model) } : undefined
      "
      :aria-checked="custom"
      :aria-label="t('tags.color.custom')"
      v-tooltip="t('tags.color.custom')"
      @click="openPicker"
    >
      <Icon v-if="custom" name="check" :size="16" />
    </button>
    <input
      ref="picker"
      type="color"
      class="color-swatches-input"
      tabindex="-1"
      aria-hidden="true"
      :value="model"
      @input="model = ($event.target as HTMLInputElement).value"
    />
  </div>
</template>
